/**
 * Catch Board — open-pipeline momentum (board-level WoW cue).
 *
 * =============================================================================
 * PRODUCT RULES (locked for v1 — change here before changing behaviour)
 * =============================================================================
 *
 * Goal
 * ----
 * Give sales a simple “are we building or shrinking?” signal on the board
 * header. Do NOT put % change on every column footer in v1 (stage progression
 * correctly shrinks earlier columns and would read as failure).
 *
 * Universe
 * --------
 * Open pipeline only: stages in CATCH_BOARD_OPEN_STAGES.
 * Exclude Landed and Got Away from current and prior baselines.
 *
 * Metrics (v1)
 * ------------
 * - dealCount
 * - dealValuePence (primary £)
 * potentialDealValuePence is out of scope for v1 (may add later).
 *
 * Comparison model
 * ----------------
 * Stock comparison: open totals *now* vs open totals at the comparison instant.
 * Not inflow / net flow (that is optional phase 2, per open column).
 *
 * Window
 * ------
 * Rolling 7 days — compare now to (now − 7 days).
 * Display copy: “vs previous 7 days (open deals)”.
 * Not calendar Mon–Sun UK weeks for v1 (avoids partial-week noise; matches
 * reconstruction). Any human date labels use Europe/London; the as-of instant
 * is a fixed duration before “now”.
 *
 * Filters
 * -------
 * Momentum always uses the same list filters as the board (owner, search,
 * readiness, etc.). Never compare a filtered board to an unfiltered prior.
 * While filters refetch, clear / hide momentum so colours do not flash from
 * the previous filter set.
 *
 * Display
 * -------
 * - Keep absolute current totals on the board; append signed % when meaningful.
 * - Prior baseline missing or prior metric === 0 → delta shows "—" (muted, never red).
 * - Direction (open pipeline): up = positive/green, down = negative/red.
 * - Neutral (no colour shout): absolute delta === 0, or |pct| < flat band.
 *
 * Baseline data (v1)
 * ------------------
 * Reconstruct open stock at as-of from createdAt / closedAt (deal was created
 * on or before as-of and not closed on or before as-of). Sum current
 * dealValuePence for that set (value edits mid-window are an accepted
 * approximation until snapshots exist).
 *
 * Out of scope (v1)
 * -----------------
 * Per-column % footers, calendar weeks, potential £, nightly snapshots.
 *
 * Keep numeric locks + pure helpers in sync with
 * express_app/services/catchBoard/catchBoardMomentum.js.
 */

/** Rolling comparison window length in days (now vs now − N). */
export const CATCH_BOARD_MOMENTUM_WINDOW_DAYS = 7

/** IANA zone for any human-facing date labels tied to momentum. */
export const CATCH_BOARD_MOMENTUM_TIMEZONE = 'Europe/London'

/**
 * |percent change| strictly below this is treated as flat (neutral).
 * Absolute zero change is always flat regardless of this band.
 */
export const CATCH_BOARD_MOMENTUM_FLAT_PCT_BAND = 0.5

/** User-facing period hint (tooltip / aria). */
export const CATCH_BOARD_MOMENTUM_PERIOD_LABEL =
  'vs previous 7 days (open deals)'

/**
 * Metrics included in v1 momentum payloads (order is display order).
 */
export const CATCH_BOARD_MOMENTUM_METRICS = Object.freeze([
  'dealCount',
  'dealValuePence',
])

/** Direction token for a single metric delta. */
export const CATCH_BOARD_MOMENTUM_DIRECTION = Object.freeze({
  UP: 'up',
  DOWN: 'down',
  FLAT: 'flat',
  NONE: 'none',
})

/**
 * Colour / tone token for UI (map to green / red / muted in the strip).
 * Open-pipeline: up is good (positive), down is bad (negative).
 */
export const CATCH_BOARD_MOMENTUM_TONE = Object.freeze({
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  NEUTRAL: 'neutral',
  MUTED: 'muted',
})

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Coerce a metric value to a finite number, else null.
 * @param {unknown} value
 * @returns {number|null}
 */
export function toCatchBoardMomentumNumber(value) {
  if (value == null || value === '') {
    return null
  }
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Prior comparison instant for a rolling window ending at `now`.
 * @param {Date|string|number} [now]
 * @param {number} [windowDays]
 * @returns {Date}
 */
export function getCatchBoardMomentumAsOfDate(
  now = new Date(),
  windowDays = CATCH_BOARD_MOMENTUM_WINDOW_DAYS,
) {
  const end = now instanceof Date ? new Date(now.getTime()) : new Date(now)
  const days = Number(windowDays)
  const safeDays =
    Number.isFinite(days) && days > 0 ? days : CATCH_BOARD_MOMENTUM_WINDOW_DAYS
  return new Date(end.getTime() - safeDays * MS_PER_DAY)
}

/**
 * Percent change from prior → current.
 * Returns null when prior is missing or zero (no meaningful %).
 * @param {unknown} current
 * @param {unknown} prior
 * @returns {number|null}
 */
export function computeCatchBoardPercentChange(current, prior) {
  const currentN = toCatchBoardMomentumNumber(current)
  const priorN = toCatchBoardMomentumNumber(prior)
  if (currentN == null || priorN == null || priorN === 0) {
    return null
  }
  return ((currentN - priorN) / priorN) * 100
}

/**
 * Direction token from current/prior (and optional precomputed %).
 * @param {{
 *   current?: unknown,
 *   prior?: unknown,
 *   percentChange?: number|null,
 *   flatPctBand?: number,
 * }} [options]
 * @returns {'up'|'down'|'flat'|'none'}
 */
export function getCatchBoardMomentumDirection({
  current,
  prior,
  percentChange,
  flatPctBand = CATCH_BOARD_MOMENTUM_FLAT_PCT_BAND,
} = {}) {
  const currentN = toCatchBoardMomentumNumber(current)
  const priorN = toCatchBoardMomentumNumber(prior)
  if (currentN == null || priorN == null || priorN === 0) {
    return CATCH_BOARD_MOMENTUM_DIRECTION.NONE
  }

  const absoluteDelta = currentN - priorN
  if (absoluteDelta === 0) {
    return CATCH_BOARD_MOMENTUM_DIRECTION.FLAT
  }

  const pct =
    percentChange == null
      ? computeCatchBoardPercentChange(currentN, priorN)
      : toCatchBoardMomentumNumber(percentChange)
  if (pct == null) {
    return CATCH_BOARD_MOMENTUM_DIRECTION.NONE
  }

  const band = toCatchBoardMomentumNumber(flatPctBand)
  const safeBand =
    band == null || band < 0 ? CATCH_BOARD_MOMENTUM_FLAT_PCT_BAND : band
  if (Math.abs(pct) < safeBand) {
    return CATCH_BOARD_MOMENTUM_DIRECTION.FLAT
  }

  return pct > 0
    ? CATCH_BOARD_MOMENTUM_DIRECTION.UP
    : CATCH_BOARD_MOMENTUM_DIRECTION.DOWN
}

/**
 * Map direction → UI tone token.
 * @param {'up'|'down'|'flat'|'none'|string|null|undefined} direction
 * @returns {'positive'|'negative'|'neutral'|'muted'}
 */
export function getCatchBoardMomentumTone(direction) {
  switch (direction) {
    case CATCH_BOARD_MOMENTUM_DIRECTION.UP:
      return CATCH_BOARD_MOMENTUM_TONE.POSITIVE
    case CATCH_BOARD_MOMENTUM_DIRECTION.DOWN:
      return CATCH_BOARD_MOMENTUM_TONE.NEGATIVE
    case CATCH_BOARD_MOMENTUM_DIRECTION.FLAT:
      return CATCH_BOARD_MOMENTUM_TONE.NEUTRAL
    default:
      return CATCH_BOARD_MOMENTUM_TONE.MUTED
  }
}

/**
 * Format a percent label: ↑12% / ↓8% / 0% / —
 * @param {number|null|undefined} percentChange
 * @param {'up'|'down'|'flat'|'none'|string|null|undefined} [direction]
 * @returns {string}
 */
export function formatCatchBoardMomentumPercentLabel(
  percentChange,
  direction,
) {
  const resolvedDirection =
    direction ||
    (percentChange == null
      ? CATCH_BOARD_MOMENTUM_DIRECTION.NONE
      : Math.abs(percentChange) < CATCH_BOARD_MOMENTUM_FLAT_PCT_BAND
        ? CATCH_BOARD_MOMENTUM_DIRECTION.FLAT
        : percentChange > 0
          ? CATCH_BOARD_MOMENTUM_DIRECTION.UP
          : percentChange < 0
            ? CATCH_BOARD_MOMENTUM_DIRECTION.DOWN
            : CATCH_BOARD_MOMENTUM_DIRECTION.FLAT)

  if (
    resolvedDirection === CATCH_BOARD_MOMENTUM_DIRECTION.NONE ||
    percentChange == null ||
    !Number.isFinite(percentChange)
  ) {
    return '—'
  }

  if (resolvedDirection === CATCH_BOARD_MOMENTUM_DIRECTION.FLAT) {
    return '0%'
  }

  const rounded = Math.round(Math.abs(percentChange))
  if (resolvedDirection === CATCH_BOARD_MOMENTUM_DIRECTION.UP) {
    return `↑${rounded}%`
  }
  if (resolvedDirection === CATCH_BOARD_MOMENTUM_DIRECTION.DOWN) {
    return `↓${rounded}%`
  }
  return '—'
}

/**
 * Build the shared momentum delta shape for one metric.
 * Pure — no I/O.
 *
 * @param {{ current?: unknown, prior?: unknown, flatPctBand?: number }} [options]
 * @returns {{
 *   current: number|null,
 *   prior: number|null,
 *   absoluteDelta: number|null,
 *   percentChange: number|null,
 *   direction: 'up'|'down'|'flat'|'none',
 *   tone: 'positive'|'negative'|'neutral'|'muted',
 *   label: string,
 * }}
 */
export function buildCatchBoardMetricMomentum({
  current,
  prior,
  flatPctBand = CATCH_BOARD_MOMENTUM_FLAT_PCT_BAND,
} = {}) {
  const currentN = toCatchBoardMomentumNumber(current)
  const priorN = toCatchBoardMomentumNumber(prior)
  const percentChange = computeCatchBoardPercentChange(currentN, priorN)
  const direction = getCatchBoardMomentumDirection({
    current: currentN,
    prior: priorN,
    percentChange,
    flatPctBand,
  })
  const absoluteDelta =
    currentN == null || priorN == null ? null : currentN - priorN

  return {
    current: currentN,
    prior: priorN,
    absoluteDelta,
    percentChange,
    direction,
    tone: getCatchBoardMomentumTone(direction),
    label: formatCatchBoardMomentumPercentLabel(percentChange, direction),
  }
}

/**
 * Normalize an API momentum change object for the header strip.
 * Prefers server label/tone; rebuilds from current/prior when needed.
 * Empty-prior / no-% cases are forced to muted "—" (never a scary red).
 * @param {object|null|undefined} change
 * @returns {{ label: string, tone: string, direction: string }|null}
 */
export function normalizeCatchBoardMomentumChange(change) {
  if (!change || typeof change !== 'object') {
    return null
  }

  let normalized = null

  if (change.label != null && change.tone) {
    normalized = {
      label: String(change.label),
      tone: String(change.tone),
      direction: String(change.direction || CATCH_BOARD_MOMENTUM_DIRECTION.NONE),
    }
  } else if (
    Object.prototype.hasOwnProperty.call(change, 'current') ||
    Object.prototype.hasOwnProperty.call(change, 'prior')
  ) {
    const built = buildCatchBoardMetricMomentum({
      current: change.current,
      prior: change.prior,
    })
    normalized = {
      label: built.label,
      tone: built.tone,
      direction: built.direction,
    }
  }

  if (!normalized) {
    return null
  }

  // No meaningful baseline → muted em dash, never up/down colour.
  if (
    normalized.direction === CATCH_BOARD_MOMENTUM_DIRECTION.NONE ||
    normalized.label === '—' ||
    toCatchBoardMomentumNumber(change.prior) === 0
  ) {
    return {
      label: '—',
      tone: CATCH_BOARD_MOMENTUM_TONE.MUTED,
      direction: CATCH_BOARD_MOMENTUM_DIRECTION.NONE,
    }
  }

  return normalized
}

/**
 * Pick display-ready count/value deltas from list `totals.momentum`.
 * Tooltip copy always uses the locked FE period label (open deals).
 * @param {object|null|undefined} momentum
 * @returns {{
 *   periodLabel: string,
 *   dealCountChange: { label: string, tone: string, direction: string }|null,
 *   dealValueChange: { label: string, tone: string, direction: string }|null,
 * }|null}
 */
export function getCatchBoardMomentumStripModel(momentum) {
  if (!momentum || typeof momentum !== 'object') {
    return null
  }

  return {
    periodLabel: CATCH_BOARD_MOMENTUM_PERIOD_LABEL,
    dealCountChange: normalizeCatchBoardMomentumChange(
      momentum.dealCountChange,
    ),
    dealValueChange: normalizeCatchBoardMomentumChange(
      momentum.dealValueChange,
    ),
  }
}
